import {
  AuthenticatorEntry,
  AUTHENTICATOR_DB_NAME,
  AUTHENTICATOR_DB_VERSION,
  AUTHENTICATOR_STORE_NAME
} from './Types'

class AuthenticatorStorage {
  private db: IDBDatabase | null = null
  private isInitialized = false

  async initialize(): Promise<void> {
    if (this.isInitialized && this.db) {
      return
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        AUTHENTICATOR_DB_NAME,
        AUTHENTICATOR_DB_VERSION
      )

      request.onerror = () => {
        reject(
          new Error(
            `Failed to open IndexedDB: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
      }

      request.onsuccess = () => {
        this.db = request.result
        this.isInitialized = true
        resolve()
      }

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(AUTHENTICATOR_STORE_NAME)) {
          const store = db.createObjectStore(AUTHENTICATOR_STORE_NAME, {
            keyPath: 'id'
          })
          store.createIndex('createdAt', 'createdAt', { unique: false })
          store.createIndex('isLocked', 'isLocked', { unique: false })
        }
      }
    })
  }

  private ensureInitialized(): void {
    if (!this.isInitialized || !this.db) {
      throw new Error(
        'AuthenticatorStorage not initialized. Call initialize() first.'
      )
    }
  }

  async addEntry(entry: AuthenticatorEntry): Promise<void> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readwrite'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const request = store.add(entry)

      request.onsuccess = () => resolve()
      request.onerror = () =>
        reject(
          new Error(
            `Failed to add authenticator: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async getEntry(id: string): Promise<AuthenticatorEntry | null> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readonly'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const request = store.get(id)

      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () =>
        reject(
          new Error(
            `Failed to get authenticator: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async getAllEntries(): Promise<AuthenticatorEntry[]> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readonly'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const index = store.index('createdAt')
      const request = index.getAll()

      request.onsuccess = () => {
        const authenticators = request.result.sort(
          (a, b) => a.createdAt - b.createdAt
        )
        resolve(authenticators)
      }
      request.onerror = () =>
        reject(
          new Error(
            `Failed to get authenticators: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async updateEntry(entry: AuthenticatorEntry): Promise<void> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readwrite'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const request = store.put(entry)

      request.onsuccess = () => resolve()
      request.onerror = () =>
        reject(
          new Error(
            `Failed to update authenticator: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async deleteEntry(id: string): Promise<void> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readwrite'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () =>
        reject(
          new Error(
            `Failed to delete authenticator: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async clearAll(): Promise<void> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readwrite'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () =>
        reject(
          new Error(
            `Failed to clear authenticators: ${
              request.error?.message || 'Unknown error'
            }`
          )
        )
    })
  }

  async importEntries(entries: AuthenticatorEntry[]): Promise<void> {
    this.ensureInitialized()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        [AUTHENTICATOR_STORE_NAME],
        'readwrite'
      )
      const store = transaction.objectStore(AUTHENTICATOR_STORE_NAME)

      // Clear existing data first
      const clearRequest = store.clear()

      clearRequest.onsuccess = () => {
        // Add imported data
        let completed = 0
        const total = entries.length

        if (total === 0) {
          resolve()
          return
        }

        entries.forEach(entry => {
          const addRequest = store.add(entry)

          addRequest.onsuccess = () => {
            completed++
            if (completed === total) {
              resolve()
            }
          }

          addRequest.onerror = () => {
            reject(
              new Error(
                `Failed to import authenticator: ${
                  addRequest.error?.message || 'Unknown error'
                }`
              )
            )
          }
        })
      }

      clearRequest.onerror = () => {
        reject(
          new Error(
            `Failed to clear existing data: ${
              clearRequest.error?.message || 'Unknown error'
            }`
          )
        )
      }
    })
  }

  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.isInitialized = false
    }
  }
}

export { AuthenticatorStorage }
